import { ref, computed } from "vue";
import axios from "axios";
import specialNotesApi from "@/vue/api/users/special_notes";
import faceSheetHistorySummaryApi from "@/vue/api/users/face_sheet_history_summary";

/* eslint-disable @typescript-eslint/no-explicit-any */

// 3画面（夜間相談 / 日中助産師相談 / いつでも相談）で共通のユーザー情報タブ用
// フェッチオーケストレーション・履歴統合ロジックを抽出したcomposable。
// 既存の ReservationUserInfoTab.vue の実装をそのまま移植したものであり、挙動は変えない。
// 編集系ロジック（toggle*NoteEdit等）はこのcomposableのスコープに含めない。

export interface UseUserInfoTabDataExcludeParams {
  kartes?: Record<string, unknown>;
  daytimeAdviceSheets?: Record<string, unknown>;
  anytimeConsultations?: Record<string, unknown>;
}

export interface UseUserInfoTabDataOptions {
  getUserId: () => number | string | null;
  // エンドポイントごとの追加クエリパラメータ（自己除外等）。省略時は付与しない
  getExcludeParams?: () => UseUserInfoTabDataExcludeParams;
}

// 夜間相談はバックエンドでenumが定義されていない 数値と文字列の両方に対応するラベルを定義しておく
const DEPARTMENT_LABELS: Record<string, string> = {
  1: "小児科医",
  2: "産婦人科医",
  3: "助産師",
  pediatrician: "小児科医",
  obstetrician: "産婦人科医",
  midwife: "助産師",
};

const departmentLabel = (departmentType: unknown): string =>
  DEPARTMENT_LABELS[String(departmentType)] || (departmentType as string) || "-";

const toTimestamp = (datetime: unknown): number => {
  if (!datetime) return 0;
  if (typeof datetime === "number") return datetime;

  const normalized = typeof datetime === "string"
    ? datetime.trim().replace(" ", "T")
    : datetime;
  const parsed = Date.parse(normalized as string);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export function useUserInfoTabData(options: UseUserInfoTabDataOptions) {
  const { getUserId, getExcludeParams } = options;

  const cancelToken = axios.CancelToken;
  let cancelSource: ReturnType<typeof cancelToken.source> | null = null;
  // fetchChildContext は cancelSource（axios）の対象外で並行実行されるため、
  // リクエストごとに世代IDを発行し、古い世代のレスポンスが新しい世代の結果を上書きしないようにする
  let childRequestId = 0;

  // ローカルstate（Vuex不使用）
  const user = ref<Record<string, any>>({});
  const specialNote = ref("");
  const specialNoteRevisionId = ref<string | null>(null);
  const userKartes = ref<any[]>([]);
  const userAnytimeConsultations = ref<any[]>([]);
  const userDaytimeAdviceSheets = ref<any[]>([]);
  const epdsAnswerList = ref<any[]>([]);
  const municipalCooperationHistories = ref<any[]>([]);
  const frequentUseResponseHistories = ref<any[]>([]);
  const highRiskReportHistories = ref<any[]>([]);
  const riskAssessmentHistories = ref<any[]>([]);
  const children = ref<any[]>([]);
  const childrenLoading = ref(false);

  const consultationHistories = computed(() => {
    const daytimeSheets = Array.isArray(userDaytimeAdviceSheets.value) ? userDaytimeAdviceSheets.value : [];
    const anytimeConsultations = Array.isArray(userAnytimeConsultations.value) ? userAnytimeConsultations.value : [];
    const nightKartes = Array.isArray(userKartes.value) ? userKartes.value : [];

    const daytimeHistories = daytimeSheets.map((sheet) => ({
      key: `daytime-${sheet.id}`,
      sourceType: "daytime",
      id: sheet.id,
      datetime: sheet.consultedAt || [sheet.adviceDate, sheet.startAt].filter(Boolean).join(" "),
      // 日中助産師相談は子と紐づく手段がないため、対象の子は常にブランク表示にする
      childName: null,
      childId: null,
      consultationType: `日中助産師相談 / ${departmentLabel(sheet.departmentType)}`,
      complaint: sheet.complaint,
    }));

    const anytimeHistories = anytimeConsultations.map((consultation) => ({
      key: `anytime-${consultation.id}`,
      sourceType: "anytime",
      id: consultation.id,
      datetime: consultation.consultedAt || consultation.simpleAskedAt,
      childName: consultation.childName,
      childId: consultation.childId ?? null,
      consultationType: `いつでも相談 / ${departmentLabel(consultation.departmentType)}`,
      complaint: consultation.complaint,
    }));

    const nightHistories = nightKartes.map((karte) => ({
      key: `night-${karte.id}`,
      sourceType: "night",
      id: karte.id,
      reservationId: karte.reservationId,
      datetime: karte.consultedAt || [karte.date, karte.time].filter(Boolean).join(" "),
      childName: karte.childName,
      childId: karte.childId ?? null,
      consultationType: `夜間相談 / ${departmentLabel(karte.departmentType)}`,
      complaint: karte.complaint,
    }));

    return [...daytimeHistories, ...anytimeHistories, ...nightHistories].sort((a, b) => {
      const left = toTimestamp(a.datetime);
      const right = toTimestamp(b.datetime);
      return right - left;
    });
  });

  const epdsHistories = computed(() => {
    const answers = Array.isArray(epdsAnswerList.value) ? epdsAnswerList.value : [];
    return answers.map((answer) => {
      const question10 = (answer.important_questions || []).find((q: any) => q.question_no === 10);
      return {
        id: answer.id,
        answeredAt: answer.answeredAt || answer.answered_at,
        period: answer.weeks_after_childbirth != null ? `産後${answer.weeks_after_childbirth}` : "-",
        totalScore: answer.total_score != null ? `${answer.total_score}点` : "-",
        positive: answer.positive === true,
        question10Score: question10 ? `${question10.score}点` : "-",
        question10Choice: question10 ? question10.choice : "",
      };
    });
  });

  const fetchUserContext = async () => {
    const userId = getUserId();

    if (!userId) {
      clear();
      return;
    }

    const excludeParams = getExcludeParams?.() ?? {};

    municipalCooperationHistories.value = [];
    frequentUseResponseHistories.value = [];
    highRiskReportHistories.value = [];
    riskAssessmentHistories.value = [];

    if (cancelSource) {
      cancelSource.cancel();
    }
    cancelSource = cancelToken.source();
    const token = cancelSource.token;

    await Promise.allSettled([
      axios.get("/api/v1/users", { params: { user_id: userId }, cancelToken: token })
        .then((r) => {
          user.value = r.data || {};
        }),

      specialNotesApi.fetchSpecialNote(String(userId), { cancelToken: token })
        .then((r) => {
          specialNote.value = r.specialNote || "";
          specialNoteRevisionId.value = r.specialNoteRevisionId || null;
        }),

      axios.get(`/api/v1/users/${userId}/kartes`, {
        params: excludeParams.kartes,
        cancelToken: token,
        validateStatus: (s) => s < 500,
      }).then((r) => { userKartes.value = r.status === 404 ? [] : (r.data || []); }),

      axios.get(`/api/v1/users/${userId}/anytime_consultations`, {
        params: excludeParams.anytimeConsultations,
        cancelToken: token,
      }).then((r) => { userAnytimeConsultations.value = r.data || []; }),

      axios.get("/api/v1/daytime_advice_sheets/users_advice_sheets", {
        params: { user_id: userId, ...excludeParams.daytimeAdviceSheets },
        cancelToken: token,
      }).then((r) => { userDaytimeAdviceSheets.value = r.data || []; }),

      axios.get("/api/v1/epds_answers", { params: { user_id: userId }, cancelToken: token })
        .then((r) => { epdsAnswerList.value = r.data || []; }),

      faceSheetHistorySummaryApi.fetchFaceSheetHistorySummary(String(userId), { cancelToken: token })
        .then((r) => {
          municipalCooperationHistories.value = r.municipalCooperationHistories || [];
          frequentUseResponseHistories.value = r.frequentUseResponseHistories || [];
          highRiskReportHistories.value = r.highRiskReportHistories || [];
          riskAssessmentHistories.value = r.riskAssessmentHistories || [];
        }),
    ]);
  };

  const fetchChildContext = async () => {
    const requestId = ++childRequestId;
    const userId = getUserId();
    if (!userId) {
      children.value = [];
      return;
    }
    childrenLoading.value = true;
    try {
      const r = await axios.get("/api/v1/children", {
        params: { user_id: userId },
        validateStatus: (s) => s < 500,
      });
      // 取得中に別のレコードへ遷移し、より新しいリクエストが発行されていた場合は
      // このレスポンスを反映しない（stale response破棄）
      if (requestId !== childRequestId) return;
      children.value = Array.isArray(r.data) ? r.data : [];
    } finally {
      if (requestId === childRequestId) {
        childrenLoading.value = false;
      }
    }
  };

  // 子の情報は他のユーザー関連情報（相談履歴等）と依存関係がないため、
  // fetchUserContext の完了を待たずに並行して取得し、表示までの待ち時間を短縮する。
  const start = async () => {
    await Promise.allSettled([fetchUserContext(), fetchChildContext()]);
  };

  const clear = () => {
    if (cancelSource) {
      cancelSource.cancel();
      cancelSource = null;
    }
    // 進行中の子情報取得リクエストがあれば、その結果を無効化する
    childRequestId += 1;
    user.value = {};
    specialNote.value = "";
    specialNoteRevisionId.value = null;
    children.value = [];
    childrenLoading.value = false;
    userKartes.value = [];
    userAnytimeConsultations.value = [];
    userDaytimeAdviceSheets.value = [];
    epdsAnswerList.value = [];
    municipalCooperationHistories.value = [];
    frequentUseResponseHistories.value = [];
    highRiskReportHistories.value = [];
    riskAssessmentHistories.value = [];
  };

  return {
    user,
    specialNote,
    specialNoteRevisionId,
    userKartes,
    userAnytimeConsultations,
    userDaytimeAdviceSheets,
    epdsAnswerList,
    municipalCooperationHistories,
    frequentUseResponseHistories,
    highRiskReportHistories,
    riskAssessmentHistories,
    children,
    childrenLoading,
    consultationHistories,
    epdsHistories,
    start,
    clear,
  };
}
